/**
 * SnapTo90.js
 * 
 * This module provides functionality for perpendicular (90-degree) snapping and angle display
 * in a 3D modeling environment. These features help users accurately create perpendicular lines
 * and see the current angle between line segments during drawing operations.
 */

import {DrawingGlobals, LABELS} from "../../Global/variables";
import * as THREE from "three";

/**
 * Distance threshold for snap detection in world units
 * Points within this distance of a snap point will snap to that point
 */
let threshold = 0.1;

/**
 * Determines if the current point should snap to a 90-degree angle
 * from the last two drawn points
 * 
 * This function is used during drawing operations to help create perpendicular
 * line segments. It checks if the current point is close enough to a position
 * that would form a 90-degree angle with the previous line segment.
 * 
 * @param {THREE.Vector3|null} prevSnapPoint - The previously snapped point, if any
 * @param {THREE.Vector3} point - The current cursor position to check for snapping
 * @returns {THREE.Vector3|null} The snap point if within threshold, otherwise null
 */
export function snap90Deg(prevSnapPoint, point) {
    // If we're already snapped to a point and still within threshold, keep using it
    if (prevSnapPoint != null && point.distanceTo(prevSnapPoint) <= threshold) {
        return prevSnapPoint;
    } else {
        // Get the intersection points array from DrawingGlobals
        let intersects = DrawingGlobals.intersectionPoints;
        
        // Current mouse position
        let A = point;
        // Last drawn point
        let B = intersects[intersects.length - 1];
        // Second-to-last drawn point
        let C = intersects[intersects.length - 2];
        
        // Find the point that would create a 90-degree angle with the line BC
        let snap90 = find90DegPoint(B, C, A);
        
        // If the calculated 90-degree point is close enough to the current position,
        // return it as the snap point
        if (snap90.distanceTo(A) <= threshold) {
            return snap90;
        } else {
            // Not close enough to snap
            return null;
        }
    }
}

/**
 * Calculates a point that forms a 90-degree angle with the line BC,
 * closest to point A
 * 
 * This function uses vector projection to find the point that:
 * 1. Lies on a line starting from B
 * 2. Forms a 90-degree angle with the line BC
 * 3. Has the same distance from B as the input point A
 * 
 * @param {THREE.Vector3} B - The origin point (last drawn point)
 * @param {THREE.Vector3} C - The point that forms the reference line with B
 * @param {THREE.Vector3} A - The current position to project
 * @returns {THREE.Vector3} The calculated point that forms a 90-degree angle
 */
function find90DegPoint(B, C, A) {
    // Vector from B to C (the reference line)
    const BC = new THREE.Vector3().subVectors(C, B);
    
    // Normalized direction of the reference line BC
    const directionBC = BC.clone().normalize();
    
    // Normalized direction from B to the mouse position
    const directionBMouse = A.clone().sub(B).normalize();

    // Project the mouse direction onto a plane perpendicular to BC
    // This is done by removing the component of directionBMouse that's parallel to directionBC
    const projectionBMouseOntoPlane = directionBMouse.clone().sub(
        directionBC.clone().multiplyScalar(directionBMouse.dot(directionBC))
    );
    
    // Get the distance from B to the current mouse position
    const lengthBX = A.distanceTo(B);
    
    // Create a vector with the perpendicular direction and same length as B to mouse
    const BX = projectionBMouseOntoPlane.clone().multiplyScalar(lengthBX);
    
    // Return the final position by adding the vector to point B
    return new THREE.Vector3().addVectors(B, BX);
}

/**
 * Displays the current angle between the last drawn line segment and
 * the line from the last point to the current cursor position
 * 
 * This function calculates the angle between two line segments and
 * updates a label in the scene to display this angle to the user.
 * 
 * @param {THREE.Vector3} point - The current cursor position
 */
export function showAngle(point) {
    // Get the intersection points array from DrawingGlobals
    const intersects = DrawingGlobals.intersectionPoints;
    
    // Need at least two points to calculate an angle
    if (intersects.length < 2) return;

    // Last drawn point (the vertex of the angle)
    const B = intersects[intersects.length - 1];
    // Second-to-last drawn point
    const C = intersects[intersects.length - 2];

    // Vector from B to current point
    const BA = point.clone().sub(B);
    // Vector from B to last drawn point
    const BC = C.clone().sub(B);

    // Calculate the dot product of the two vectors
    const dotProduct = BA.dot(BC);
    // Calculate the magnitudes of the two vectors
    const magnitudeBA = BA.length();
    const magnitudeBC = BC.length();

    // Calculate the angle in degrees using the dot product formula:
    // angle = arccos(dot(v1, v2) / (|v1| * |v2|))
    const angle = Math.acos(dotProduct / (magnitudeBA * magnitudeBC)) * THREE.MathUtils.RAD2DEG;

    // Update the angle label
    LABELS.angle.visible = true;
    LABELS.angle.element.innerText = angle.toFixed(2) + "°";
    
    // Position the label between the cursor and the last point
    // The 0.8 factor places it closer to the cursor than to the last point
    LABELS.angle.position.lerpVectors(point, B, 0.8);
}